import singletonRouter from "next/router";
import { Configure, InstantSearch, InstantSearchServerState, InstantSearchSSRProvider } from "react-instantsearch";
import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";

import CustomInfiniteHits from "~/components/search/custom-infinite-hits";
import { searchClient } from "~/components/search/search-client";
import Algolia from "~/types/algolia";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";

import AlgoliaProductListing from "./algolia-product-listing";
import Facets from "./facets";

type Props = {
  serverState?: InstantSearchServerState;
  algoliaIndexId: string;
  categoryId: string;
  components?: any;
  listScope: string;
  serverUrl: string;
};

export default function ProductGridSkeleton(props: Props) {
  const { serverState, serverUrl, categoryId, components, listScope, algoliaIndexId } = props;

  const categoryLevel = categoryId.split("_").length;

  const algoliaPLPConfig = CommonUtils.parseJsonSafely(
    process.env.NEXT_PUBLIC_ALGOLIA_PLP_CONFIG!
  ) as Algolia.Configuration;

  return (
    <InstantSearchSSRProvider key={algoliaIndexId + "-" + props.categoryId} {...serverState}>
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexId}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        insights
        routing={{
          stateMapping: {
            stateToRoute: (uiState: any) => {
              const indexUiState = uiState[algoliaIndexId];

              const { refinementList, range, ratingMenu, sortBy, ...rest } = indexUiState;

              return {
                [algoliaIndexId]: {
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
              };
            },
            routeToState(routeState) {
              if (!routeState?.[algoliaIndexId]) {
                return routeState;
              }

              const { refinementList, range, ratingMenu, sortBy, ...rest } = routeState[algoliaIndexId];

              return {
                [algoliaIndexId]: {
                  refinementList,
                  range,
                  ratingMenu,
                  sortBy,
                },
              };
            },
          },
          router: createInstantSearchRouterNext({
            singletonRouter,
            serverUrl,
            routerOptions: { cleanUrlOnDispose: false },
          }),
        }}
      >
        <Configure
          {...algoliaPLPConfig}
          disjunctiveFacetsRefinements={{ [`categories.lvl${categoryLevel}`]: [categoryId] }}
        />
        <Facets />
        {serverState ? (
          <AlgoliaProductListing
            components={components}
            listScope={listScope}
            categoryId={props.categoryId}
            totalNumberProducts={serverState.initialResults[algoliaIndexId].results?.[0].nbHits ?? 0}
          />
        ) : (
          <CustomInfiniteHits
            showPrevious={false}
            listScope={listScope}
            numberImagesLoadedWithPriority={Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
          />
        )}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}
